import { TMasterOccupation } from "@/api/entities/master-occupation.entity";
import { useGetMasterOccupationTree, useReorderMasterOccupations } from "@/api/services/global/master";
import { IconOrderTable } from "@/assets/icons/IconOrderTable";
import { ConfirmLeavePage } from "@/components/commons/ConfirmLeavePage";
import { FooterActionBar } from "@/components/commons/FooterActionBar";
import { Button } from "@/components/elements/Button";
import { InnerBox } from "@/components/elements/InnerBox";
import { LoadingPage } from "@/components/elements/Loading/Loading";
import { Switch } from "@/components/elements/Switch";
import { TopPage } from "@/components/elements/TopPage";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { EMPTY_STR } from "@/constants/string";
import { CategoryFormModal } from "@/features/master-data/components/Occupation/CategoryFormModal";
import { HiddenOccupationModal } from "@/features/master-data/components/Occupation/HiddenOccupationModal";
import { MASTER_DATA_TYPE_TITLE } from "@/features/master-data/constants/masterDataType";
import { useVisible } from "@/hooks/useVisible";
import { AppRoutes } from "@/routes/config";
import { background, blue, button, divider, green, text } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { showError } from "@/utils/error";
import { toNumber } from "@/utils/number";
import { toast } from "@/utils/toast";
import { Box, Stack, styled, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";
import { Link } from "react-router-dom";

/** Excel の1行に相当する平坦な行。カテゴリ・職種は rowSpan でまとめる。 */
type TOccupationRow = {
  category: TMasterOccupation;
  shokushu: TMasterOccupation | null;
  sagyo: TMasterOccupation | null;
  /** この行がカテゴリの先頭行なら、カテゴリセルの rowSpan 数 */
  categoryRowSpan: number;
  /** この行が職種の先頭行なら、職種セルの rowSpan 数 */
  shokushuRowSpan: number;
  /** 並び替え用。カテゴリの categories 内での位置 */
  categoryIndex: number;
};

/** カテゴリが占める行数（作業が1つも無い職種も1行は使う） */
const countCategoryRows = (category: TMasterOccupation) =>
  category.children?.length ? category.children.reduce((total, shokushu) => total + Math.max(shokushu.children?.length ?? 0, 1), 0) : 1;

/** ツリーを Excel と同じ平坦な行の並びへ変換する */
const flattenTree = (categories: TMasterOccupation[]): TOccupationRow[] =>
  categories.flatMap((category, categoryIndex) => {
    const categoryRowSpan = countCategoryRows(category);
    if (!category.children?.length) {
      return [{ category, shokushu: null, sagyo: null, categoryRowSpan, shokushuRowSpan: 1, categoryIndex }];
    }

    let isFirstRowOfCategory = true;
    return category.children.flatMap((shokushu) => {
      const shokushuRowSpan = Math.max(shokushu.children?.length ?? 0, 1);
      const sagyoList: (TMasterOccupation | null)[] = shokushu.children?.length ? shokushu.children : [null];

      return sagyoList.map((sagyo, index) => {
        const row: TOccupationRow = {
          category,
          shokushu,
          sagyo,
          categoryRowSpan: isFirstRowOfCategory ? categoryRowSpan : 0,
          shokushuRowSpan: index === 0 ? shokushuRowSpan : 0,
          categoryIndex,
        };
        isFirstRowOfCategory = false;
        return row;
      });
    });
  });

export const MasterDataOccupation = () => {
  const reorderMutation = useReorderMasterOccupations();
  const createModal = useVisible();
  const editModal = useVisible();

  const { data, isLoading } = useGetMasterOccupationTree();

  const [categories, setCategories] = useState<TMasterOccupation[]>([]);
  const [enableSaveOrderBtn, setEnableSaveOrderBtn] = useState(false);
  const [hiddenOccupation, setHiddenOccupation] = useState<TMasterOccupation>();
  const [selectedCategory, setSelectedCategory] = useState<TMasterOccupation>();

  useEffect(() => {
    if (data && data.length > 0) {
      setCategories(data);
    }
  }, [data]);

  const rows = useMemo(() => flattenTree(categories), [categories]);

  const handleSaveOrder = async () => {
    try {
      const rs = await reorderMutation.mutateAsync({ occupations: categories.map((category) => ({ id: toNumber(category.id) })) });
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      setEnableSaveOrderBtn(false);
    } catch (error) {
      showError(error);
    }
  };

  // カテゴリ単位の並び替え。職種・作業の並び替えは各行の編集から行う。
  const onDragEnd = (result: DropResult) => {
    if (!result.destination || !result.source || result.source.index === result.destination.index) {
      return;
    }
    const reordered = Array.from(categories);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);
    setCategories(reordered);
    setEnableSaveOrderBtn(true);
  };

  const openEdit = (category: TMasterOccupation) => {
    setSelectedCategory(category);
    editModal.open();
  };

  const closeEdit = () => {
    editModal.close();
    setSelectedCategory(undefined);
  };

  if (isLoading) {
    return <LoadingPage />;
  }

  return (
    <>
      <TopPage title={`「${MASTER_DATA_TYPE_TITLE.MASTER_OCCUPATION}」の編集`} enablePageBack={false} />
      <InnerBox gap={3}>
        <Stack direction="row" justifyContent="flex-end" mb={3}>
          <Button size="sm" variant="primary" onClick={createModal.open}>
            項目追加
          </Button>
        </Stack>
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="tableRows" direction="vertical">
            {(provided) => (
              <StyledTable {...provided.droppableProps} ref={provided.innerRef}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      <TableCell>カテゴリ</TableCell>
                      <TableCell>職種</TableCell>
                      <TableCell>作業</TableCell>
                      <TableCell>試験名</TableCell>
                      <TableCell>1号修了時の級</TableCell>
                      <TableCell>2号修了時の級</TableCell>
                      <TableCell>3号修了時の級</TableCell>
                      <TableCell sx={{ position: "sticky", textAlign: "center !important", right: "100px", bgcolor: background.white }}>
                        有効／無効
                      </TableCell>
                      <TableCell sx={{ position: "sticky", textAlign: "center !important", right: "0px", bgcolor: background.white }}>編集</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => {
                      const { category, shokushu, sagyo, categoryRowSpan, shokushuRowSpan, categoryIndex } = row;
                      const rowKey = `${category.id}-${shokushu?.id ?? ""}-${sagyo?.id ?? ""}`;

                      const body = (
                        <>
                          {categoryRowSpan > 0 && (
                            <TableCell rowSpan={categoryRowSpan} sx={{ minWidth: "220px", paddingLeft: "8px !important" }}>
                              <Typography color={category.isHidden ? text.tertiary : text.primary} variant="body14">
                                {category.name || EMPTY_STR.TEXT}
                              </Typography>
                            </TableCell>
                          )}
                          {shokushuRowSpan > 0 && (
                            <TableCell rowSpan={shokushuRowSpan} sx={{ minWidth: "240px", paddingLeft: "8px !important" }}>
                              <Typography color={shokushu?.isHidden ? text.tertiary : text.primary} variant="body14">
                                {shokushu?.name || EMPTY_STR.TEXT}
                              </Typography>
                            </TableCell>
                          )}
                          <TableCell sx={{ minWidth: "260px", paddingLeft: "8px !important" }}>
                            <Typography color={sagyo?.isHidden ? text.tertiary : text.primary} variant="body14">
                              {sagyo?.name || EMPTY_STR.TEXT}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ minWidth: "380px", paddingLeft: "8px !important" }}>
                            <Typography color={sagyo?.isHidden ? text.tertiary : text.primary} variant="body14">
                              {sagyo?.examName || EMPTY_STR.TEXT}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ minWidth: "120px", paddingLeft: "8px !important" }}>
                            <Typography color={sagyo?.isHidden ? text.tertiary : text.primary} variant="body14">
                              {sagyo?.level1Name || EMPTY_STR.TEXT}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ minWidth: "120px", paddingLeft: "8px !important" }}>
                            <Typography color={sagyo?.isHidden ? text.tertiary : text.primary} variant="body14">
                              {sagyo?.level2Name || EMPTY_STR.TEXT}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ minWidth: "120px", paddingLeft: "8px !important" }}>
                            <Typography color={sagyo?.isHidden ? text.tertiary : text.primary} variant="body14">
                              {sagyo?.level3Name || EMPTY_STR.TEXT}
                            </Typography>
                          </TableCell>
                          {/* 有効／無効・編集はカテゴリ単位。職種・作業の切り替えは編集画面から行う。 */}
                          {categoryRowSpan > 0 && (
                            <>
                              <TableCell
                                rowSpan={categoryRowSpan}
                                sx={{
                                  textAlign: "center !important",
                                  minWidth: "120px",
                                  position: "sticky",
                                  right: "100px",
                                  bgcolor: background.white,
                                }}
                              >
                                <Switch
                                  sx={{ marginRight: 0, marginLeft: 0 }}
                                  checked={!category.isHidden}
                                  onChange={() => setHiddenOccupation(category)}
                                  color="primary"
                                />
                              </TableCell>
                              <TableCell
                                rowSpan={categoryRowSpan}
                                sx={{
                                  minWidth: "100px",
                                  textAlign: "center !important",
                                  paddingX: "4px !important",
                                  position: "sticky",
                                  right: "0px",
                                  bgcolor: background.white,
                                }}
                              >
                                <Button size="sm" onClick={() => openEdit(category)}>
                                  編集
                                </Button>
                              </TableCell>
                            </>
                          )}
                        </>
                      );

                      // カテゴリの先頭行だけがドラッグ対象。カテゴリ単位で並び替える。
                      if (categoryRowSpan > 0) {
                        return (
                          <Draggable key={rowKey} draggableId={category.id.toString()} index={categoryIndex}>
                            {(draggableProvided) => (
                              <TableRow ref={draggableProvided.innerRef} {...draggableProvided.draggableProps}>
                                <TableCell
                                  rowSpan={categoryRowSpan}
                                  sx={{ minWidth: "40px", paddingLeft: "10px !important" }}
                                  {...draggableProvided.dragHandleProps}
                                >
                                  <IconOrderTable color={blue[80]} />
                                </TableCell>
                                {body}
                              </TableRow>
                            )}
                          </Draggable>
                        );
                      }

                      return <TableRow key={rowKey}>{body}</TableRow>;
                    })}
                    {provided.placeholder}
                  </TableBody>
                </Table>
              </StyledTable>
            )}
          </Droppable>
        </DragDropContext>
      </InnerBox>
      <FooterActionBar mt={4}>
        <Stack sx={{ flexDirection: "row", justifyContent: "space-between" }}>
          <Button component={Link} to={AppRoutes.masterData} variant="ghost" size="sm">
            戻る
          </Button>
          <Button size="sm" variant="outline" onClick={handleSaveOrder} disabled={!enableSaveOrderBtn} isLoading={reorderMutation.isPending}>
            オーダーを保存する
          </Button>
        </Stack>
      </FooterActionBar>

      {createModal.visible && <CategoryFormModal isOpen={createModal.visible} onClose={createModal.close} />}

      {editModal.visible && selectedCategory && (
        <CategoryFormModal isOpen={editModal.visible} onClose={closeEdit} selectedCategory={selectedCategory} />
      )}

      <HiddenOccupationModal hiddenOccupation={hiddenOccupation} setHiddenOccupation={setHiddenOccupation} />
      <ConfirmLeavePage shouldBlock={enableSaveOrderBtn} />
    </>
  );
};

const StyledTable = styled(Box)`
  border-radius: ${rounded.sm};
  overflow: auto;
  max-height: calc(100vh - 380px);
  border-top: 1px solid ${divider.middle};
  border-bottom: 1px solid ${divider.middle};
  border-right: 1px solid ${divider.middle};
  background-color: ${background.white};
  // 横スクロールバーを細くし、最終行に被る範囲を最小限にする
  scrollbar-width: thin;
  &::-webkit-scrollbar {
    height: 8px;
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    border-radius: 4px;
    background-color: ${divider.middle};
  }
  &::-webkit-scrollbar-track {
    background-color: transparent;
  }
  table {
    border-collapse: separate;
  }
  th,
  td {
    border-left: 1px solid ${divider.middle};
  }
  * {
    box-shadow: none;
  }
  .MuiToolbar-dense {
    display: none;
  }
  .MuiTableCell-head {
    padding: 8px !important;
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
    text-align: left;
    position: sticky;
    background-color: white;
    z-index: 500;
    top: 0;
  }

  .Mui-TableHeadCell-Content-Labels {
    > span {
      margin-left: 4px;
      img {
        opacity: 0.2;
      }
      .MuiBadge-badge {
        display: none;
      }
    }
  }
  .MuiTableCell-body {
    font-size: 14px;
    font-weight: 400;
    line-height: 28px;
  }

  // Default
  .MuiTableRow-root {
    background-color: ${background.white};
    box-shadow: none;
    cursor: initial;
    .MuiTableCell-root {
      padding: 0;
      border-color: ${divider.middle};
      opacity: 1;
    }
    &:not(.MuiTableRow-head):last-child {
      .MuiTableCell-root {
        border-bottom: 0;
      }
    }
  }
  // Hover
  .MuiTableRow-root:not(.MuiTableRow-head):hover {
    &,
    td {
      background-color: ${background.white};
      &::after {
        background-color: ${background.primary};
      }
    }
  }
  // Click/Pressed
  .Mui-selected {
    background-color: ${background.white} !important;
    td::after {
      background: ${background.secondary}!important;
    }
  }

  .MuiPaper-root {
    box-shadow: none;
  }
  .MuiPaper-root > .MuiBox-root {
    display: none;
  }

  .MuiCheckbox-root {
    color: ${button.primary}!important;
    height: 24px;
    width: 24px;
    padding: 0;
  }

  // Disabled Row
  .MuiTableCell-root.disabled {
    opacity: 1;
    background: ${background.disable}!important;
    pointer-events: none;
  }

  // added row
  .MuiTableCell-root.added {
    background: ${green[99]}!important;
    &::after {
      background: unset !important;
    }
  }

  // Failed row
  .MuiTableCell-root.failed {
    background: ${background.error}!important;
    &::after {
      background: unset !important;
    }
  }

  // Clickable row
  .MuiTableCell-root.clickable {
    cursor: pointer;
  }
`;
